import { useState, useEffect, useMemo, useRef } from 'react';
import { addDays, differenceInDays, format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isToday, isWeekend } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Crosshair } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACTIVITY_STATUS_SWATCH, TAG_DOT } from '@/lib/tag-palette';
import type { MarketingActivity } from '@/types';

interface GanttViewProps {
  activities: MarketingActivity[];
  loading: boolean;
  onRefresh: () => void;
  onActivityClick: (id: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  backlog: TAG_DOT[ACTIVITY_STATUS_SWATCH.backlog],
  todo: TAG_DOT[ACTIVITY_STATUS_SWATCH.todo],
  in_progress: TAG_DOT[ACTIVITY_STATUS_SWATCH.in_progress],
  review: TAG_DOT[ACTIVITY_STATUS_SWATCH.review],
  done: TAG_DOT[ACTIVITY_STATUS_SWATCH.done],
};

type ZoomLevel = 'day' | 'week' | 'month' | 'year';

export function GanttView({ activities, loading, onActivityClick }: GanttViewProps) {
  const [zoom, setZoom] = useState<ZoomLevel>('week');
  const [offset, setOffset] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const DAY_WIDTH = zoom === 'day' ? 40 : zoom === 'week' ? 20 : zoom === 'month' ? 8 : 3;
  const VISIBLE_DAYS = zoom === 'day' ? 30 : zoom === 'week' ? 60 : zoom === 'month' ? 180 : 365;

  const validActivities = useMemo(() =>
    activities.filter((a) => a.start_date && a.end_date),
    [activities]
  );

  const dateRange = useMemo(() => {
    if (validActivities.length === 0) {
      const today = new Date();
      return { start: addDays(today, -7), end: addDays(today, VISIBLE_DAYS) };
    }
    const dates = validActivities.flatMap((a) => [parseISO(a.start_date!), parseISO(a.end_date!)]);
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    return {
      start: addDays(startOfWeek(min, { locale: de }), offset - 7),
      end: addDays(endOfWeek(max, { locale: de }), offset + 14),
    };
  }, [validActivities, offset, VISIBLE_DAYS]);

  const days = useMemo(() => eachDayOfInterval({ start: dateRange.start, end: dateRange.end }), [dateRange]);
  const totalWidth = days.length * DAY_WIDTH;

  useEffect(() => {
    if (scrollRef.current && offset === 0) {
      const todayIdx = days.findIndex((d) => isToday(d));
      if (todayIdx > -1) {
        scrollRef.current.scrollLeft = Math.max(0, todayIdx * DAY_WIDTH - 200);
      }
    }
  }, [days, DAY_WIDTH, offset]);

  const getBarStyle = (activity: MarketingActivity) => {
    const start = parseISO(activity.start_date!);
    const end = parseISO(activity.end_date!);
    const startOffset = differenceInDays(start, dateRange.start);
    const duration = Math.max(1, differenceInDays(end, start) + 1);
    return {
      left: startOffset * DAY_WIDTH,
      width: duration * DAY_WIDTH - 2,
    };
  };

  const scrollToToday = () => {
    setOffset(0);
    if (scrollRef.current) {
      const todayIdx = days.findIndex((d) => isToday(d));
      if (todayIdx > -1) {
        scrollRef.current.scrollLeft = Math.max(0, todayIdx * DAY_WIDTH - 200);
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary-600" /></div>;
  }

  if (validActivities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted">
        <p className="text-lg font-medium">Keine Aktivitäten mit Datumszeitraum</p>
        <p className="mt-1 text-sm">Setze Start- und Enddatum bei Aktivitäten, um sie hier zu sehen.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-1">
          <button title="Zeitraum zurück" onClick={() => setOffset((o) => o - VISIBLE_DAYS / 2)} className="rounded p-1 hover:bg-card-hover"><ChevronLeft size={16} /></button>
          <button title="Zur heutigen Position springen" onClick={scrollToToday} className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-card-hover"><Crosshair size={14} />Heute</button>
          <button title="Zeitraum vor" onClick={() => setOffset((o) => o + VISIBLE_DAYS / 2)} className="rounded p-1 hover:bg-card-hover"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-1">
          <button title="Ansicht Jahr" onClick={() => setZoom('year')} className={cn('rounded px-2 py-1 text-xs font-medium', zoom === 'year' && 'bg-card-hover text-foreground dark:bg-primary-hover/30 dark:text-muted')}>Jahr</button>
          <button title="Ansicht Monat" onClick={() => setZoom('month')} className={cn('rounded px-2 py-1 text-xs font-medium', zoom === 'month' && 'bg-card-hover text-foreground dark:bg-primary-hover/30 dark:text-muted')}>Monat</button>
          <button title="Ansicht Woche" onClick={() => setZoom('week')} className={cn('rounded px-2 py-1 text-xs font-medium', zoom === 'week' && 'bg-card-hover text-foreground dark:bg-primary-hover/30 dark:text-muted')}>Woche</button>
          <button title="Ansicht Tag" onClick={() => setZoom('day')} className={cn('rounded px-2 py-1 text-xs font-medium', zoom === 'day' && 'bg-card-hover text-foreground dark:bg-primary-hover/30 dark:text-muted')}>Tag</button>
          <span className="ml-2 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted">
            <ZoomOut size={12} />
            Filter: {zoom === 'day' ? 'Tag' : zoom === 'week' ? 'Woche' : zoom === 'month' ? 'Monat' : 'Jahr'}
            <ZoomIn size={12} />
          </span>
        </div>
      </div>

      <div className="flex">
        {/* Activity labels */}
        <div className="w-52 shrink-0 border-r border-border">
          <div className="h-10 border-b border-border px-3 py-2 text-xs font-medium text-muted">Aktivität</div>
          {validActivities.map((a) => (
            <div
              key={a.id}
              onClick={() => onActivityClick(a.id)}
              className="flex h-10 cursor-pointer items-center border-b border-border px-3 hover:bg-background dark:hover:bg-card-hover"
            >
              <span className="truncate text-sm text-muted">{a.title}</span>
            </div>
          ))}
        </div>

        {/* Timeline area */}
        <div ref={scrollRef} className="flex-1 overflow-x-auto">
          <div style={{ width: totalWidth, minWidth: '100%' }}>
            {/* Header with dates */}
            <div className="flex h-10 border-b border-border">
              {days.map((day, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex h-full shrink-0 items-center justify-center border-r border-border text-[10px]',
                    isToday(day) && 'bg-card-hover font-bold text-foreground dark:bg-primary-hover/20 dark:text-muted',
                    isWeekend(day) && 'bg-background dark:bg-card-hover/50',
                  )}
                  style={{ width: DAY_WIDTH }}
                >
                  {zoom === 'day' ? format(day, 'dd.MM', { locale: de }) :
                   zoom === 'week' && day.getDay() === 1 ? format(day, 'dd.MM', { locale: de }) :
                   zoom === 'month' && day.getDate() === 1 ? format(day, 'MMM', { locale: de }) :
                   zoom === 'year' && day.getDate() === 1 ? format(day, 'MMM', { locale: de }) : ''}
                </div>
              ))}
            </div>

            {/* Bars */}
            {validActivities.map((activity) => {
              const style = getBarStyle(activity);
              const isMilestone = activity.start_date === activity.end_date;

              return (
                <div key={activity.id} className="relative h-10 border-b border-border">
                  {isMilestone ? (
                    <div
                      className={cn('absolute top-2 h-5 w-5 rotate-45 cursor-pointer', STATUS_COLORS[activity.status] || 'bg-muted')}
                      style={{ left: style.left + style.width / 2 - 10 }}
                      onClick={() => onActivityClick(activity.id)}
                      title={activity.title}
                    />
                  ) : (
                    <div
                      className={cn('absolute top-2 h-6 cursor-pointer rounded-md shadow-sm transition-opacity hover:opacity-80', STATUS_COLORS[activity.status] || 'bg-muted')}
                      style={{ left: style.left, width: Math.max(style.width, 4) }}
                      onClick={() => onActivityClick(activity.id)}
                      title={`${activity.title}\n${activity.start_date} - ${activity.end_date}`}
                    >
                      {style.width > 60 && (
                        <span className="absolute inset-x-1 top-0.5 truncate text-[10px] font-medium text-white">{activity.title}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
